import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Tag, List, message, Spin, Alert, Image } from 'antd';
import { ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import priceListService from '../../services/priceListService';

const REVIEW_COLOR = {
  PENDING: 'gold',
  APPROVED: 'green',
  MODIFYING: 'blue',
  SENT: 'purple',
  SUPERSEDED: 'default',
  REJECTED: 'red',
};

export default function PublishPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canPublish = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_PUBLISH_ACTION);

  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await priceListService.getPublishStatus();
      setStatus(data);
    } catch (e) {
      message.error(e?.message || 'Failed to load publish status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const onPublish = async () => {
    setPublishing(true);
    try {
      const res = await priceListService.publish();
      if (res?.success === false) {
        message.error(res?.message || 'Publish failed');
        return;
      }
      message.success(res?.message || 'Published and broadcast successfully');
      loadStatus();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const latest = status?.latest_run;
  const updatedProducts = status?.updated_products ?? [];

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Publish price list</h1>
          <p className="text-sm text-gray-500 mt-1">
            Mid-day publish after pricing edits. Morning deck is emailed for Approve / Modify at 8:25 AM IST.
          </p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadStatus} loading={loading}>
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spin size="large" />
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card title="Latest deck" className="rounded-2xl shadow-sm">
            {latest ? (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2 items-center">
                  <Tag color={REVIEW_COLOR[latest.review_status] || 'default'}>
                    {latest.review_status || '—'}
                  </Tag>
                  <span className="text-sm text-gray-500">{latest.run_date}</span>
                  <span className="text-sm text-gray-500">{latest.product_count} products</span>
                </div>
                {latest.image_url && (
                  <Image
                    src={latest.image_url}
                    alt="Price list deck"
                    className="rounded-lg border border-gray-100"
                  />
                )}
                {latest.sent_at && (
                  <p className="text-xs text-gray-500">
                    Last sent: {new Date(latest.sent_at).toLocaleString()}
                  </p>
                )}
                <Link to="/pricing/special-tiers" className="text-emerald-700 underline text-sm">
                  Modify pricing tiers
                </Link>
              </div>
            ) : (
              <Alert type="info" message="No deck generated yet." showIcon />
            )}
          </Card>

          <Card title="Publish status" className="rounded-2xl shadow-sm">
            <p className="text-sm text-gray-600 mb-4">
              {status?.last_sent_at
                ? `Last broadcast: ${new Date(status.last_sent_at).toLocaleString()}`
                : 'No broadcast recorded yet.'}
            </p>

            {updatedProducts.length > 0 ? (
              <>
                <Alert
                  type="warning"
                  showIcon
                  className="mb-4"
                  message={`${updatedProducts.length} product(s) updated since last send`}
                />
                <List
                  size="small"
                  bordered
                  dataSource={updatedProducts}
                  renderItem={(item) => <List.Item>{item.name}</List.Item>}
                  className="mb-4 max-h-48 overflow-y-auto"
                />
                {canPublish && (
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={publishing}
                    onClick={onPublish}
                  >
                    Publish now
                  </Button>
                )}
              </>
            ) : (
              <Alert
                type="success"
                showIcon
                message="No unpublished pricing changes since last broadcast."
              />
            )}
          </Card>
        </div>
      )}
    </DashboardPageWrapper>
  );
}
